/**
 * Readable rendering of a structured prep-profile section.
 *
 * Empty sub-fields are skipped rather than printed as a label with nothing
 * after it. Sensitive constraints are the exception: "nothing to protect" is
 * itself an answer, so an empty value there reads "None identified.".
 */

const METADATA_KEYS = new Set([
  'is_starting_position',
  'quality_verified',
  'has_sensitive_content',
  'sensitivity_note',
])

const EXPLICIT_WHEN_EMPTY: Record<string, string> = {
  sensitive_constraints: 'None identified.',
}

export function formatProfileKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function isEmpty(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.every(isEmpty)
  return false
}

function renderScalar(value: unknown): string {
  if (Array.isArray(value)) {
    return value.filter((v) => !isEmpty(v)).map(String).join(', ')
  }
  return String(value)
}

export function renderProfileValue(value: unknown): string {
  if (typeof value === 'string') return value.trim() || '—'
  if (Array.isArray(value)) return renderScalar(value) || '—'
  if (value && typeof value === 'object') {
    const parts: string[] = []
    for (const [key, sub] of Object.entries(value as Record<string, unknown>)) {
      if (METADATA_KEYS.has(key)) continue
      if (isEmpty(sub)) {
        if (key in EXPLICIT_WHEN_EMPTY) {
          parts.push(`${formatProfileKey(key)}: ${EXPLICIT_WHEN_EMPTY[key]}`)
        }
        continue
      }
      parts.push(`${formatProfileKey(key)}: ${renderScalar(sub)}`)
    }
    return parts.length > 0 ? parts.join(' · ') : '—'
  }
  return String(value ?? '—')
}
